import React from 'react';
import { 
  Edit3, 
  Trash2, 
  Calendar, 
  User, 
  MapPin, 
  Package, 
  Clock,
  Banknote,
  FileText,
  AlertTriangle,
  CheckCircle,
  Paperclip,
  Shield,
  PenTool
} from 'lucide-react';
import { formatZAR } from '../utils/currency';

const ServiceLogList = ({ logs, loading, onEdit, onDelete }) => {
  // Debug: Log the data received
  console.log('🔍 DEBUGGING: ServiceLogList received logs:', logs);
  console.log('🔍 DEBUGGING: Number of logs to display:', logs.length);
  
  if (logs.length > 0) {
    console.log('🔍 DEBUGGING: First log data:', logs[0]);
    console.log('🔍 DEBUGGING: First log keys:', Object.keys(logs[0]));
    console.log('🔍 DEBUGGING: First log propertyName:', logs[0].propertyName);
    console.log('🔍 DEBUGGING: First log scheduledDate:', logs[0].scheduledDate);
    console.log('🔍 DEBUGGING: First log attachments:', logs[0].attachments);
  }
  
  const getStatusColor = (status) => {
    switch (status) {
      case 'Completed': return 'bg-green-100 text-green-800';
      case 'In Progress': return 'bg-blue-100 text-blue-800';
      case 'Scheduled': return 'bg-yellow-100 text-yellow-800';
      case 'Overdue': return 'bg-red-100 text-red-800';
      case 'Cancelled': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'Critical': return 'text-red-600';
      case 'High': return 'text-orange-600';
      case 'Medium': return 'text-yellow-600';
      case 'Low': return 'text-green-600';
      default: return 'text-gray-600';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Not set';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatDuration = (minutes) => {
    if (!minutes) return 'N/A';
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hours > 0) {
      return `${hours}h ${mins}m`;
    }
    return `${mins}m`;
  };

  const getSignatureStatus = (log) => {
    const workflow = log.signature_workflow;
    if (!workflow || !workflow.signatures || workflow.signatures.length === 0) {
      return {
        hasSignatures: false,
        technicianSigned: false,
        approvalSigned: false,
        signatureCount: 0
      };
    }

    const technicianSigned = workflow.signatures.some(sig => sig.signature_type === 'technician');
    const approvalSigned = workflow.signatures.some(sig => sig.signature_type === 'approval');
    
    return {
      hasSignatures: true,
      technicianSigned,
      approvalSigned,
      signatureCount: workflow.signatures.length
    };
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8">
        <div className="flex items-center justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-cyan-600"></div>
          <span className="ml-3 text-gray-600">Loading service logs...</span>
        </div>
      </div>
    );
  }

  if (logs.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center">
        <FileText size={48} className="mx-auto text-gray-400 mb-4" />
        <h3 className="text-lg font-medium text-gray-900 mb-2">No service logs found</h3>
        <p className="text-gray-600">
          No service logs match your current filters. Try adjusting your search criteria.
        </p>
      </div>
    );
  }

  return (
    <div className="w-full space-y-3 sm:space-y-4">
      {logs.map((log) => (
        <div key={log.id} className="w-full bg-white rounded-lg shadow-sm border border-gray-200 hover:shadow-md transition-shadow">
          <div className="p-4 sm:p-6">
            {/* Header - Fully responsive */}
            <div className="flex flex-col space-y-3 mb-4">
              
              {/* Service Type, Status, and Priority - Mobile responsive */}
              <div className="flex flex-col xs:flex-row xs:items-center gap-2">
                <h3 className="text-base sm:text-lg font-semibold text-gray-900 truncate">
                  {log.serviceType}
                </h3>
                <div className="flex items-center gap-2 flex-wrap">
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(log.status)} whitespace-nowrap`}>
                    {log.status}
                  </span>
                  <span className={`text-xs sm:text-sm font-medium ${getPriorityColor(log.priority)} whitespace-nowrap`}>
                    {log.priority} Priority
                  </span>
                  
                  {/* Signature Status Indicator */}
                  {(() => {
                    const sigStatus = getSignatureStatus(log);
                    if (sigStatus.hasSignatures) {
                      return (
                        <div className="flex items-center gap-1 px-2 py-1 bg-green-100 text-green-800 rounded-full text-xs font-medium whitespace-nowrap">
                          <Shield size={12} />
                          <span>{sigStatus.signatureCount} Signature{sigStatus.signatureCount !== 1 ? 's' : ''}</span>
                        </div>
                      );
                    } else if (log.status === 'Completed') {
                      return (
                        <div className="flex items-center gap-1 px-2 py-1 bg-orange-100 text-orange-800 rounded-full text-xs font-medium whitespace-nowrap">
                          <PenTool size={12} />
                          <span>Pending Signature</span>
                        </div>
                      );
                    }
                    return null;
                  })()} 
                </div>
              </div>

              {/* Property Name - Full width on mobile */}
              <div className="flex items-center bg-blue-50 px-3 py-2 rounded-md border border-blue-200 w-full">
                <MapPin size={16} className="text-blue-600 mr-2 flex-shrink-0" />
                <span className="text-sm font-semibold text-blue-800 truncate">
                  {log.propertyName || log.property_name || 'No Property Name'}
                </span>
                {/* Debug info - remove in production */}
                {(!log.propertyName && !log.property_name) && (
                  <span className="text-xs text-red-500 ml-2 flex-shrink-0">
                    (DEBUG: Missing)
                  </span>
                )}
              </div>

              {/* Actions - Mobile optimized */}
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <p className="text-sm text-gray-600 line-clamp-2">{log.description}</p>
                </div>
                <div className="flex items-center space-x-1 ml-4 flex-shrink-0">
                  {onEdit && (
                    <button
                      onClick={() => onEdit(log)}
                      className="min-w-[44px] min-h-[44px] p-2 text-gray-400 hover:text-cyan-600 hover:bg-cyan-50 rounded-lg transition-colors flex items-center justify-center"
                      title="Edit service log"
                      aria-label="Edit service log"
                    >
                      <Edit3 size={18} />
                    </button>
                  )}
                  {onDelete && (
                    <button
                      onClick={() => onDelete(log.id)}
                      className="min-w-[44px] min-h-[44px] p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors flex items-center justify-center"
                      title="Delete service log"
                      aria-label="Delete service log"
                    >
                      <Trash2 size={18} />
                    </button>
                  )}
                </div>
              </div>
            </div>

            {/* Scheduled Date - Prominently displayed and mobile responsive */}
            <div className="mb-4">
              <div className="flex items-center bg-yellow-50 px-3 py-2 rounded-md border border-yellow-200 w-full">
                <Calendar size={16} className="text-yellow-600 mr-2 flex-shrink-0" />
                <div className="flex-1">
                  <p className="text-xs text-yellow-700 font-medium">Scheduled Date</p>
                  <p className="text-sm font-bold text-yellow-800">
                    {formatDate(log.scheduledDate || log.scheduled_date) || 'No Date'}
                  </p>
                  {/* Debug info - remove in production */}
                  {(!log.scheduledDate && !log.scheduled_date) && (
                    <span className="text-xs text-red-500">
                      (DEBUG: Missing)
                    </span>
                  )}
                </div>
                
                {/* Asset Info - Side by side on larger screens */}
                {log.assetName && (
                  <div className="flex items-center space-x-1 ml-4">
                    <Package size={14} className="text-gray-400 flex-shrink-0" />
                    <span className="text-xs text-gray-600 truncate max-w-[120px]">{log.assetName}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Details Grid - Fully responsive */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 mb-4">
              <div className="flex items-center space-x-2 min-w-0">
                <User size={16} className="text-gray-400 flex-shrink-0" />
                <div className="min-w-0 flex-1">
                  <p className="text-xs text-gray-500">Technician</p>
                  <p className="text-sm font-medium text-gray-900 truncate">{log.technician}</p>
                </div>
              </div>

              {log.completedDate && (
                <div className="flex items-center space-x-2 min-w-0">
                  <CheckCircle size={16} className="text-green-500 flex-shrink-0" />
                  <div className="min-w-0 flex-1">
                    <p className="text-xs text-gray-500">Completed</p>
                    <p className="text-sm font-medium text-gray-900 truncate">{formatDate(log.completedDate)}</p>
                  </div>
                </div>
              )}

              <div className="flex items-center space-x-2 min-w-0">
                <Clock size={16} className="text-gray-400 flex-shrink-0" />
                <div className="min-w-0 flex-1">
                  <p className="text-xs text-gray-500">Duration</p>
                  <p className="text-sm font-medium text-gray-900">{formatDuration(log.duration)}</p>
                </div>
              </div>
            </div>

            {/* Cost and Additional Info - Mobile responsive */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div className="flex flex-wrap items-center gap-4">
                {log.cost && (
                  <div className="flex items-center space-x-1">
                    <Banknote size={16} className="text-gray-400 flex-shrink-0" />
                    <span className="text-sm font-medium text-gray-900">{formatZAR(log.cost)}</span>
                  </div>
                )}
                
                {log.attachments && log.attachments.length > 0 && (
                  <div className="flex items-center space-x-1">
                    <Paperclip size={16} className="text-gray-400 flex-shrink-0" />
                    <span className="text-sm text-gray-600">
                      {log.attachments.length} attachment(s)
                    </span>
                  </div>
                )}
                
                
              </div>

              {/* Status indicator for overdue items */}
              {log.status === 'Overdue' && (
                <div className="flex items-center space-x-1 text-red-600">
                  <AlertTriangle size={16} className="flex-shrink-0" />
                  <span className="text-sm font-medium">Overdue</span>
                </div>
              )}
            </div>

            {/* Notes (if any) - Mobile responsive */}
            {log.notes && (
              <div className="mt-4 p-3 bg-gray-50 rounded-lg">
                <p className="text-xs text-gray-500 mb-1">Notes:</p>
                <p className="text-sm text-gray-700 break-words">{log.notes}</p>
              </div>
            )}
          </div>
        </div>
      ))}
    </div>
  );
};

export default ServiceLogList;